import React, { useEffect, useMemo, useRef, useState } from "react";
import { Collapse, Icon } from "antd";

import JobSwmsFormV3DropIndicator from "./reorder/JobSwmsFormV3DropIndicator.jsx";
import JobSwmsFormV3SectionPanelHeader from "./reorder/JobSwmsFormV3SectionPanelHeader.jsx";
import renderSectionContent from "./reorder/JobSwmsFormV3SectionContent.jsx";
import useJobSwmsFormV3SectionSequenceSync from "./reorder/useJobSwmsFormV3SectionSequenceSync.js";
import { FIXED_SECTION_KEYS, resolveSectionConfig } from "./reorder/JobSwmsFormV3SectionRegistry.config.js";
import { areSectionOrdersEqual, autoScrollForDrag, DROP_LINE_GAP_OFFSET } from "./reorder/JobSwmsFormV3SectionRegistry.utils.js";

import styles from "../../../JobStyle.module.css";
import { mapApprovalSubmissionHistory } from "./SwmsFormV3Serializer.js";
import VersionHistorySection from "../VersionHistorySection.js";

const { Panel } = Collapse;

export default function JobSwmsFormV3SectionRegistry({ swmsData, loading, change, approvalSubmissionHistory, onResendReviewer, onCancelApprover, swmsStatus }) {
  const sectionsMap = swmsData && swmsData.sections && typeof swmsData.sections === "object"
    ? swmsData.sections
    : {};
  const sectionKeys = Object.keys(sectionsMap);
  const sectionKeysSignature = sectionKeys.join("|");
  const fixedSectionKeys = useMemo(
    () => Object.keys(FIXED_SECTION_KEYS).filter((sectionKey) => sectionKeys.includes(sectionKey)),
    [sectionKeysSignature]
  );
  const computedNonFixedSectionKeys = useMemo(
    () => sectionKeys.filter((sectionKey) => !FIXED_SECTION_KEYS[sectionKey]),
    [sectionKeysSignature]
  );
  const [nonFixedSectionOrder, setNonFixedSectionOrder] = useState(computedNonFixedSectionKeys);
  const [draggingSectionKey, setDraggingSectionKey] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [dropLineY, setDropLineY] = useState(null);
  const collapseContainerRef = useRef(null);

  const mappedVersionHistory = useMemo(() => {
    if (!approvalSubmissionHistory || !Array.isArray(approvalSubmissionHistory)) {
      return [];
    }

    return mapApprovalSubmissionHistory(approvalSubmissionHistory, swmsData);
  }, [approvalSubmissionHistory, swmsData]);

  useEffect(() => {
    setNonFixedSectionOrder((previousOrder) => {
      const retained = previousOrder.filter((sectionKey) => computedNonFixedSectionKeys.includes(sectionKey));
      const appended = computedNonFixedSectionKeys.filter((sectionKey) => !retained.includes(sectionKey));
      const nextOrder = [...retained, ...appended];
      return areSectionOrdersEqual(previousOrder, nextOrder) ? previousOrder : nextOrder;
    });
  }, [computedNonFixedSectionKeys]);

  const orderedSectionKeys = useMemo(
    () => [...fixedSectionKeys, ...nonFixedSectionOrder],
    [fixedSectionKeys, nonFixedSectionOrder]
  );

  useJobSwmsFormV3SectionSequenceSync({ swmsData, orderedSectionKeys, sectionsMap, change });

  const moveNonFixedSection = (fromKey, toKey, placement = "before") => {
    if (!fromKey || !toKey || fromKey === toKey) {
      return;
    }

    setNonFixedSectionOrder((previousOrder) => {
      const sourceIndex = previousOrder.indexOf(fromKey);
      const destinationIndex = previousOrder.indexOf(toKey);

      if (sourceIndex < 0 || destinationIndex < 0) {
        return previousOrder;
      }

      const nextOrder = [...previousOrder];
      const [movedKey] = nextOrder.splice(sourceIndex, 1);

      let insertIndex = destinationIndex;
      if (sourceIndex < destinationIndex) {
        insertIndex -= 1;
      }
      if (placement === "after") {
        insertIndex += 1;
      }

      nextOrder.splice(insertIndex, 0, movedKey);
      return nextOrder;
    });
  };

  if (orderedSectionKeys.length === 0) {
    return <div className="text-muted">No sections found in draft data.</div>;
  }

  return (
    <div
      ref={collapseContainerRef}
      style={{ position: "relative" }}
      onDragOver={(event) => {
        if (!draggingSectionKey || loading) {
          return;
        }

        event.preventDefault();
        autoScrollForDrag(event);
      }}
      onDrop={(event) => {
        if (!draggingSectionKey || loading || !dropIndicator) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        moveNonFixedSection(draggingSectionKey, dropIndicator.sectionKey, dropIndicator.position);
        setDraggingSectionKey(null);
        setDropIndicator(null);
        setDropLineY(null);
      }}
    >
      <JobSwmsFormV3DropIndicator draggingSectionKey={draggingSectionKey} dropLineY={dropLineY} />

      <Collapse
        accordion
        defaultActiveKey={orderedSectionKeys.includes("document_project_details") ? "document_project_details" : orderedSectionKeys[0]}
        bordered={false}
        expandIconPosition="right"
        style={{ marginTop: "10px" }}
        className="custom-section-v2-collapse custom-job-swms-collapse"
        expandIcon={({ isActive }) => (
          <Icon type="caret-right" rotate={isActive ? 90 : 0} style={{ color: "#2f1f41" }} />
        )}
      >
        {orderedSectionKeys.map((sectionKey) => {
          const sectionData = sectionsMap[sectionKey];
          const config = resolveSectionConfig(sectionKey, sectionData);
          const content = renderSectionContent(sectionKey, sectionData, swmsData, loading, change, config, onCancelApprover, swmsStatus);
          const isFixedSection = config.bucket === "fixed";
          const isDraggableSection = !isFixedSection;
          const isDraggingSection = draggingSectionKey === sectionKey;
          const panelDragShadow = isDraggingSection ? "0 8px 20px rgba(31, 41, 55, 0.18)" : "none";
          const panelShadow = panelDragShadow || "none";

          return (
            <Panel
              key={sectionKey}
              className="custom-job-swmsv3-collapse-panel"
              style={{
                border: "0px !important",
                position: "relative",
                opacity: isDraggingSection ? 0.75 : 1,
                transform: isDraggingSection ? "scale(0.995)" : "none",
                boxShadow: panelShadow,
                transition: "opacity 120ms ease, transform 120ms ease, box-shadow 120ms ease"
              }}
              header={
                <JobSwmsFormV3SectionPanelHeader
                  config={config}
                  sectionKey={sectionKey}
                  isDraggableSection={isDraggableSection}
                  isDraggingSection={isDraggingSection}
                  loading={loading}
                  onDragStart={(event) => {
                    if (!isDraggableSection || loading) {
                      event.preventDefault();
                      return;
                    }

                    const target = event.target;
                    if (target && typeof target.closest === "function" && target.closest(".float-right.position-relative")) {
                      event.preventDefault();
                      return;
                    }

                    setDraggingSectionKey(sectionKey);
                  }}
                  onDragEnd={() => {
                    setDraggingSectionKey(null);
                    setDropIndicator(null);
                    setDropLineY(null);
                  }}
                  onDragEnter={(event) => {
                    if (!isDraggableSection || loading || !draggingSectionKey || draggingSectionKey === sectionKey) {
                      return;
                    }

                    event.preventDefault();
                    const bounds = event.currentTarget.getBoundingClientRect();
                    const isBefore = event.clientY < (bounds.top + bounds.height / 2);
                    setDropIndicator({ sectionKey, position: isBefore ? "before" : "after" });
                    if (collapseContainerRef.current) {
                      const containerBounds = collapseContainerRef.current.getBoundingClientRect();
                      const nextY = isBefore
                        ? (bounds.top - containerBounds.top - DROP_LINE_GAP_OFFSET)
                        : (bounds.bottom - containerBounds.top + DROP_LINE_GAP_OFFSET);
                      setDropLineY(nextY);
                    }
                  }}
                  onDragOver={(event) => {
                    if (!isDraggableSection || loading) {
                      return;
                    }

                    event.preventDefault();
                    autoScrollForDrag(event);
                    if (draggingSectionKey && draggingSectionKey !== sectionKey) {
                      const bounds = event.currentTarget.getBoundingClientRect();
                      const isBefore = event.clientY < (bounds.top + bounds.height / 2);
                      const nextIndicator = { sectionKey, position: isBefore ? "before" : "after" };
                      if (!dropIndicator || dropIndicator.sectionKey !== nextIndicator.sectionKey || dropIndicator.position !== nextIndicator.position) {
                        setDropIndicator(nextIndicator);
                      }
                      if (collapseContainerRef.current) {
                        const containerBounds = collapseContainerRef.current.getBoundingClientRect();
                        const nextY = isBefore
                          ? (bounds.top - containerBounds.top - DROP_LINE_GAP_OFFSET)
                          : (bounds.bottom - containerBounds.top + DROP_LINE_GAP_OFFSET);
                        if (dropLineY !== nextY) {
                          setDropLineY(nextY);
                        }
                      }
                    }
                  }}
                  onDrop={(event) => {
                    if (!isDraggableSection || loading) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    let placement = "before";
                    if (dropIndicator && dropIndicator.sectionKey === sectionKey) {
                      placement = dropIndicator.position;
                    } else {
                      const bounds = event.currentTarget.getBoundingClientRect();
                      placement = event.clientY < (bounds.top + bounds.height / 2) ? "before" : "after";
                    }

                    moveNonFixedSection(draggingSectionKey, sectionKey, placement);
                    setDraggingSectionKey(null);
                    setDropIndicator(null);
                    setDropLineY(null);
                  }}
                />
              }
            >
              {content}
            </Panel>
          );
        })}
        <Panel
          key={"version_history_section_swms-v3"}
          className="custcustom-job-swmsv3-collapse-panel"
          style={{ border: '0px !important' }}
          header={
            <div className={`${styles.BackgroundSectionHeading} custom-section-v2-panel-heading`}>Version &amp; Approval History</div>
          }>
          <VersionHistorySection
            versions={mappedVersionHistory.length > 0 ? mappedVersionHistory : []}
            loading={loading}
            onResendReviewer={onResendReviewer}
          />
        </Panel>
      </Collapse>
    </div>
  );
}
